import React, {useMemo} from 'react';
import DashboardLayout from "../../layouts/DashboardLayout";
import {ArrowDownIcon, ArrowUpIcon, ChartBarIcon} from '@heroicons/react/20/solid'
import {CreditCardIcon, DocumentDuplicateIcon, UsersIcon, CurrencyDollarIcon} from '@heroicons/react/24/outline'
import {clsx} from "clsx";
import {apollo} from "../../utils/apollo";
import {useQuery} from "react-query";
import {GET_MERCHANT_STATISTICS, GET_MERCHANT_SUPPLIERS_STATISTICS} from "../../utils/graphql";
import {formatNumberFloat, formatNumberInt} from "../../utils/helpers";
import Loading from "../../components/Loading";
import {useTranslation} from "react-i18next";

// const stats = [
// 	{id: 1, name: 'Number of transactions', stat: '71,897', icon: DocumentDuplicateIcon, change: '122', changeType: 'increase'},
// 	{id: 2, name: 'Successful payments', stat: '58.16%', icon: CreditCardIcon, change: '5.4%', changeType: 'increase'},
// 	{id: 3, name: 'New customers', stat: '34', icon: UsersIcon, change: '12', changeType: 'decrease'},
// ]
const icons = {
    DocumentDuplicateIcon,
    CreditCardIcon,
    UsersIcon
};

function Dashboard() {
    const {t} = useTranslation();

    const fetchMerchantStatistics = useQuery({
        queryKey: 'dashboard_statistics',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_STATISTICS,
                variables: {},
            });
            return data?.getMerchantStatistics;
        },
        keepPreviousData: false
    });

    const fetchMerchantSuppliersStatistics = useQuery({
        queryKey: 'dashboard_suppliers_statistics',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_STATISTICS,
                variables: {},
            });
            return data?.getMerchantSuppliersStatistics;
        },
        keepPreviousData: false
    });

    const stats = fetchMerchantStatistics?.isSuccess ? fetchMerchantStatistics?.data : null;
    const suppliersStats = fetchMerchantSuppliersStatistics?.isSuccess ? fetchMerchantSuppliersStatistics?.data : null;

    return (
        <DashboardLayout>
            <div>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.overview_title')}</h2>
                    <p className="text-gray-600 mt-1">{t('dashboard.overview_description')}</p>
                </div>

                <div className="mt-5">
                    {fetchMerchantStatistics.isLoading && (
                        <div className="flex justify-center items-center py-12">
                            <Loading/>
                        </div>
                    )}

                    {fetchMerchantStatistics.isSuccess && !stats?.length && (
                        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
                            <ChartBarIcon className="mx-auto h-12 w-12 text-gray-300" />
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('dashboard.no_statistics')}</h3>
                            <p className="mt-1 text-sm text-gray-500">{t('dashboard.statistics_empty')}</p>
                        </div>
                    )}

                    {fetchMerchantStatistics.isSuccess && !!stats?.length && (
                        <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                            {stats?.map((item) => {
                                let IconComponent = icons[item.icon] || null;
                                return <div
                                    key={item.id}
                                    className="relative overflow-hidden rounded-xl bg-white border border-gray-200 shadow-md"
                                >
                                    <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                                        <div className="flex items-center gap-2">
                                            <div className="p-2 rounded-lg bg-white/20">
                                                {IconComponent ? (
                                                    <IconComponent className="h-5 w-5 text-white" aria-hidden="true"/>
                                                ) : (
                                                    <ChartBarIcon className="h-5 w-5 text-white" aria-hidden="true"/>
                                                )}
                                            </div>
                                            <h3 className="text-sm font-medium text-white truncate">{item.name}</h3>
                                        </div>
                                    </div>
                                    <div className="px-6 py-4">
                                        <div className="flex items-baseline justify-between">
                                            <p className="text-2xl font-bold text-gray-900">{prepareStatsValue(item)}</p>
                                            {(item.change && item.changeType) && (
                                                <div
                                                    className={clsx(
                                                        'flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
                                                        item.changeType === 'increase' 
                                                            ? 'bg-green-100 text-green-800 border border-green-200' 
                                                            : 'bg-red-100 text-red-800 border border-red-200'
                                                    )}
                                                >
                                                    {item.changeType === 'increase' ? (
                                                        <ArrowUpIcon className="h-3 w-3" aria-hidden="true"/>
                                                    ) : (
                                                        <ArrowDownIcon className="h-3 w-3" aria-hidden="true"/>
                                                    )}
                                                    <span>{item.change}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            })}
                        </dl>
                    )}
                </div>

                <div className="mt-10">
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="p-2 rounded-lg bg-white/20">
                                    <CurrencyDollarIcon className="h-5 w-5 text-white" />
                                </div>
                                <h3 className="text-lg font-medium text-white">{t('dashboard.supplier_balances')}</h3>
                            </div>
                            <span className="text-xs font-medium text-white bg-white/20 px-3 py-1 rounded-full">
                                {suppliersStats?.length || 0} {t('dashboard.suppliers_count')}
                            </span>
                        </div>

                        {fetchMerchantSuppliersStatistics.isLoading && (
                            <div className="flex justify-center items-center py-12">
                                <Loading />
                            </div>
                        )}

                        {fetchMerchantSuppliersStatistics.isSuccess && !suppliersStats?.length && (
                            <div className="p-8 text-center">
                                <CurrencyDollarIcon className="mx-auto h-12 w-12 text-gray-300" />
                                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('dashboard.no_supplier_data')}</h3>
                                <p className="mt-1 text-sm text-gray-500">{t('dashboard.no_supplier_info')}</p>
                            </div>
                        )}

                        {fetchMerchantSuppliersStatistics.isSuccess && !!suppliersStats?.length && (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col"
                                                className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                {t('dashboard.table.supplier_id')}
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                {t('dashboard.table.supplier_name')}
                                            </th>
                                            <th scope="col"
                                                className="px-6 py-3 ltr:text-left rtl:text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                {t('dashboard.table.available_amount')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {suppliersStats?.map((record) => (
                                            <tr key={record?.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{record?.id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{record?.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-blue-600">{record?.value}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

            </div>
        </DashboardLayout>
    );
}


function prepareStatsValue(item) {
    let value = item.stat;
    if (item.valueType === 'float') value = formatNumberFloat(value);
    else if (item.valueType === 'integer') value = formatNumberInt(value);
    return `${item?.prefix ? item?.prefix + ' ' : ''}${value}${item?.suffix ? ' ' + item?.suffix : ''}`;
}

export default Dashboard;
